/**
 * ToolListItem Component (Task 6.3)
 *
 * Individual tool item in the tool list.
 * Displays tool properties and action buttons for editing, deleting, and duplicating.
 *
 * Features:
 * - Display tool name, min/max degrees, duration, easing
 * - Color indicator matching tool color
 * - Action buttons: Edit (Pencil), Delete (Trash2), Duplicate (Copy)
 * - Disable delete for prebuilt tools (show tooltip explaining why)
 */

import { Pencil, Trash2, Copy } from 'lucide-react';
import { motion } from 'framer-motion';
import type { ToolDefinition } from '@/stores/toolStore';

interface ToolListItemProps {
  /**
   * The tool to display
   */
  tool: ToolDefinition;

  /**
   * Callback when edit button is clicked
   */
  onEdit: () => void;

  /**
   * Callback when delete button is clicked
   */
  onDelete: () => void;

  /**
   * Callback when duplicate button is clicked
   */
  onDuplicate: () => void;
}

export function ToolListItem({
  tool,
  onEdit,
  onDelete,
  onDuplicate,
}: ToolListItemProps) {
  const canDelete = !tool.isPrebuilt;

  const handleDeleteClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (canDelete) {
      onDelete();
    }
  };

  const handleEditClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onEdit();
  };

  const handleDuplicateClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onDuplicate();
  };

  // Format easing function for display
  const easingLabel = tool.defaultEasing
    .replace(/-/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());

  return (
    <motion.div
      data-testid={`tool-list-item-${tool.id}`}
      className="group relative flex items-center gap-3 rounded-md p-2.5 transition-colors"
      style={{
        backgroundColor: 'transparent',
      }}
      whileHover={{
        backgroundColor: 'rgba(255, 255, 255, 0.03)',
      }}
    >
      {/* Color indicator */}
      <span
        className="h-3 w-3 flex-shrink-0 rounded-full"
        style={{ backgroundColor: tool.color }}
      />

      {/* Tool info */}
      <div className="min-w-0 flex-1">
        {/* Tool name */}
        <div
          className="truncate text-sm font-medium"
          style={{ color: 'var(--color-dark-text)' }}
        >
          {tool.name}
          {tool.isPrebuilt && (
            <span
              className="ml-1.5 rounded px-1 py-0.5 text-xs"
              style={{
                backgroundColor: 'var(--color-dark-border)',
                color: 'var(--color-dark-text-muted)',
              }}
            >
              Built-in
            </span>
          )}
        </div>

        {/* Tool properties */}
        <div
          className="mt-0.5 flex flex-wrap gap-x-3 gap-y-0.5 text-xs"
          style={{ color: 'var(--color-dark-text-muted)' }}
        >
          <span>
            {tool.minDegrees}deg to {tool.maxDegrees}deg
          </span>
          <span>{tool.defaultDuration}s</span>
          <span>{easingLabel}</span>
        </div>
      </div>

      {/* Action buttons - show on hover */}
      <div className="flex items-center gap-1 opacity-0 transition-opacity group-hover:opacity-100">
        {/* Edit button */}
        <motion.button
          onClick={handleEditClick}
          className="rounded p-1.5 transition-colors"
          style={{ color: 'var(--color-dark-text-muted)' }}
          whileHover={{
            scale: 1.1,
            color: 'var(--color-dark-text)',
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
          }}
          whileTap={{ scale: 0.9 }}
          aria-label={`Edit ${tool.name}`}
          title={`Edit ${tool.name}`}
        >
          <Pencil className="h-3.5 w-3.5" />
        </motion.button>

        {/* Duplicate button */}
        <motion.button
          onClick={handleDuplicateClick}
          className="rounded p-1.5 transition-colors"
          style={{ color: 'var(--color-dark-text-muted)' }}
          whileHover={{
            scale: 1.1,
            color: 'var(--color-dark-text)',
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
          }}
          whileTap={{ scale: 0.9 }}
          aria-label={`Duplicate ${tool.name}`}
          title={`Duplicate ${tool.name}`}
        >
          <Copy className="h-3.5 w-3.5" />
        </motion.button>

        {/* Delete button */}
        <motion.button
          onClick={handleDeleteClick}
          disabled={!canDelete}
          className="rounded p-1.5 transition-colors disabled:cursor-not-allowed disabled:opacity-40"
          style={{
            color: canDelete
              ? 'var(--color-body-control)'
              : 'var(--color-dark-text-muted)',
          }}
          whileHover={
            canDelete
              ? {
                  scale: 1.1,
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                }
              : undefined
          }
          whileTap={canDelete ? { scale: 0.9 } : undefined}
          aria-label={`Delete ${tool.name}`}
          title={
            canDelete
              ? `Delete ${tool.name}`
              : 'Built-in tools cannot be deleted. Duplicate to customize.'
          }
        >
          <Trash2 className="h-3.5 w-3.5" />
        </motion.button>
      </div>
    </motion.div>
  );
}
